// frontend/src/App.tsx

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DocumentsProvider } from "./contexts/DocumentsContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Login from "./views/Login";
import DocumentList from "./views/DocumentList";
import SingleDocumentView from "./views/SingleDocumentView";
import ComparisonView from "./views/ComparisonView";
import UserManagement from "./views/admin/UserManagement";
import DocumentManagement from "./views/admin/DocumentManagement";
import PatientProfile from "./views/PatientProfile";

interface RouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<RouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Caricamento...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<RouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Caricamento...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<RouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Caricamento...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <Navigate to={location.state?.from?.pathname || "/documents"} replace />
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <DocumentsProvider>
          <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 bg-gray-50 flex flex-col">
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <PrivateRoute>
                      <DocumentList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/documents/:id"
                  element={
                    <PrivateRoute>
                      <SingleDocumentView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/documents/compare/:ids"
                  element={
                    <PrivateRoute>
                      <ComparisonView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <UserManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/documents"
                  element={
                    <AdminRoute>
                      <DocumentManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/patient"
                  element={
                    <PrivateRoute>
                      <PatientProfile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/"
                  element={<Navigate to="/documents" replace />}
                />
                <Route
                  path="*"
                  element={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-gray-600">Pagina non trovata</div>
                    </div>
                  }
                />
              </Routes>
            </main>
            <Footer />
          </div>
        </DocumentsProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
